/**
 * FreeSWITCH Sofia 1.10 often recv WS REGISTER but never sends 401/200 when Contact/Via use
 * *.invalid (JsSIP default). Use registrar + contact on VOICE_WEBRTC_SIP_DOMAIN instead.
 *
 * @param {object} sip — voice-service /voice/webrtc/credentials
 * @param {string} tabSessionId
 * @param {import('jssip').WebSocketInterface} socket
 */
export function buildJsSipUaConfiguration(sip, tabSessionId, socket) {
  const uri = typeof sip.uri === 'string' ? sip.uri.trim() : '';
  const domainFromUri = uri.includes('@') ? uri.replace(/^sip:/i, '').split('@')[1]?.trim() : '';
  const domain = (typeof sip.domain === 'string' && sip.domain.trim())
    || domainFromUri
    || '';
  const username = (typeof sip.username === 'string' && sip.username.trim())
    || (uri ? uri.replace(/^sip:/i, '').split('@')[0]?.trim() : '');

  const registrarUri = (typeof sip.registrarUri === 'string' && sip.registrarUri.trim())
    || (typeof sip.registrar_uri === 'string' && sip.registrar_uri.trim())
    || (domain ? `sip:${domain}` : '');

  const contactTransport = resolveJsSipContactTransport(sip);
  const contactUri = domain && username
    ? `sip:${username}@${domain};transport=${contactTransport}`
    : null;

  const config = {
    sockets: [socket],
    uri,
    password: sip.password,
    session_timers: false,
    register: true,
    // FE owns reconnect (clean unregister + optional tab_session rotate). Keep JsSIP recovery
    // slow so a second UA is not stacked on the same agent_<tabId> during transport flaps.
    connection_recovery_min_interval: 60,
    connection_recovery_max_interval: 120,
    user_agent: `MercatelyVoice/${tabSessionId.slice(0, 8)}`
  };

  if (registrarUri) {
    config.registrar_server = registrarUri;
  }
  if (contactUri) {
    config.contact_uri = contactUri;
  }
  if (domain) {
    config.realm = domain;
  }
  if (username) {
    config.authorization_user = username;
  }

  return config;
}

/**
 * @param {object} sip
 * @returns {'ws'|'wss'}
 */
export function resolveJsSipContactTransport(sip) {
  const fromApi = sip.contactTransport ?? sip.contact_transport;
  if (fromApi === 'wss' || fromApi === 'ws') {
    return fromApi;
  }
  const via = (typeof sip.viaTransport === 'string' && sip.viaTransport.trim())
    || (typeof sip.via_transport === 'string' && sip.via_transport.trim())
    || '';
  if (via.toUpperCase() === 'WS') {
    return 'ws';
  }
  const wsUrl = typeof sip.wsUrl === 'string' ? sip.wsUrl.trim().toLowerCase() : '';
  if (wsUrl.startsWith('wss:') || wsUrl.includes('/fs-ws')) {
    return 'wss';
  }
  return 'ws';
}

function isFirefoxBrowser() {
  return typeof navigator !== 'undefined'
    && /firefox/i.test(navigator.userAgent || '');
}

/**
 * TURN/STUN from voice-service credentials → JsSIP RTCSession.answer({ pcConfig }).
 *
 * @param {{ iceServers?: RTCIceServer[], iceTransportPolicy?: string }} credentials
 * @returns {RTCConfiguration|undefined}
 */
export function buildAgentPeerConnectionConfig(credentials) {
  const iceServers = credentials?.iceServers;
  const policy = credentials?.iceTransportPolicy;
  /** @type {RTCConfiguration} */
  const pcConfig = {};
  if (Array.isArray(iceServers) && iceServers.length > 0) {
    pcConfig.iceServers = normalizeIceServersForBrowser(iceServers);
  }
  if (policy === 'relay' || policy === 'all') {
    pcConfig.iceTransportPolicy = policy;
  }
  // Do not set bundlePolicy/rtcpMuxPolicy — FreeSWITCH SDP + Chrome can fail negotiation.
  // iceCandidatePoolSize > 0 has caused Chrome to stall gathering after ICE prewarm.
  pcConfig.iceCandidatePoolSize = 0;
  return Object.keys(pcConfig).length > 0 ? pcConfig : undefined;
}

/**
 * Strip ?transport=… / ;transport=… so we can rebuild UDP + TCP twins.
 * @param {string} url
 * @returns {string}
 */
export function stripIceServerTransport(url) {
  return url
    .replace(/[?&]transport=[^&]*/gi, '')
    .replace(/;transport=[^;]*/gi, '')
    .replace(/\?&/, '?')
    .replace(/\?$/, '')
    .replace(/&&+/g, '&');
}

/**
 * Derive turn:…?transport=tcp from a turn: UDP (or transport=udp) URL. No new hosts.
 * @param {string} url
 * @returns {string|null}
 */
export function deriveTurnTcpUrl(url) {
  if (typeof url !== 'string' || !/^turn:/i.test(url) || /^turns:/i.test(url)) {
    return null;
  }
  if (/[?&;]transport=tcp\b/i.test(url)) {
    return null;
  }
  const base = stripIceServerTransport(url);
  const joiner = base.includes('?') ? '&' : '?';
  return `${base}${joiner}transport=tcp`;
}

/**
 * Normalize iceServers. TURN TCP twins only for Firefox — Chrome already works on UDP
 * relay and inventing TCP can break / delay ICE when coturn TCP is unhealthy.
 *
 * @param {RTCIceServer[]|undefined} iceServers
 * @param {{ expandTcpTurn?: boolean }} [options]
 * @returns {RTCIceServer[]}
 */
export function normalizeIceServersForBrowser(iceServers, options = {}) {
  if (!Array.isArray(iceServers)) return [];

  // Never invent TURN TCP here — Chrome breaks when coturn TCP is unhealthy;
  // Firefox never gathered tcp candidates from the twin anyway. Backend owns urls.
  const expandTcpTurn = options.expandTcpTurn === true;

  /** @type {RTCIceServer[]} */
  const normalized = [];

  iceServers.forEach((entry) => {
    if (!entry) return;
    const rawUrls = entry.urls ?? entry.url;
    const urls = (Array.isArray(rawUrls) ? rawUrls : [rawUrls])
      .filter((url) => typeof url === 'string' && url.trim())
      .map((url) => url.trim());
    if (urls.length === 0) return;

    const username = typeof entry.username === 'string' ? entry.username : undefined;
    let credential;
    if (typeof entry.credential === 'string') {
      credential = entry.credential;
    } else if (typeof entry.password === 'string') {
      credential = entry.password;
    }

    /** @type {Set<string>} */
    const urlSet = new Set(urls);
    if (expandTcpTurn) {
      urls.forEach((url) => {
        const tcpTwin = deriveTurnTcpUrl(url);
        if (tcpTwin) urlSet.add(tcpTwin);
      });
    }

    /** @type {RTCIceServer} */
    const next = { urls: [...urlSet] };
    if (username) next.username = username;
    if (credential) next.credential = credential;
    normalized.push(next);
  });

  return normalized;
}

/** FreeSWITCH on VPS often needs TURN relay; used only for ICE prewarm probes. */
export function enforceRelayOnlyPeerConnectionConfig(pcConfig) {
  if (!pcConfig) return pcConfig;
  const iceServers = normalizeIceServersForBrowser(pcConfig.iceServers ?? []);
  const turnOnly = iceServers.filter((entry) => {
    const urls = Array.isArray(entry.urls) ? entry.urls : [entry.urls];
    return urls.some(
      (url) => typeof url === 'string' && (url.startsWith('turn:') || url.startsWith('turns:'))
    );
  });
  /** @type {RTCConfiguration} */
  const next = {
    ...pcConfig,
    iceServers: turnOnly.length > 0 ? turnOnly : iceServers,
    iceTransportPolicy: 'relay'
  };
  if (isFirefoxBrowser()) {
    next.iceCandidatePoolSize = 0;
  }
  return next;
}

export function peerConnectionConfigHasTurn(pcConfig) {
  const iceServers = pcConfig?.iceServers ?? [];
  return iceServers.some((entry) => {
    const urls = Array.isArray(entry.urls) ? entry.urls : [entry.urls];
    return urls.some(
      (url) => typeof url === 'string' && (url.startsWith('turn:') || url.startsWith('turns:'))
    );
  });
}

/**
 * Answer PC must use credentials as returned by voice-service (iceServers + iceTransportPolicy).
 * Do not invent TURN TCP or rewrite iceTransportPolicy — Firefox "all" gathered only host
 * candidates and Nest timed out POST /answer (502); Chrome/Firefox need relay on this VPS.
 *
 * @param {RTCConfiguration|undefined} pcConfig
 * @returns {RTCConfiguration|undefined}
 */
export function resolveAgentAnswerPeerConnectionConfig(pcConfig) {
  if (!pcConfig) return undefined;
  const iceServers = normalizeIceServersForBrowser(pcConfig.iceServers ?? []);
  /** @type {RTCConfiguration} */
  const next = {
    ...pcConfig,
    iceServers,
    ...(pcConfig.iceTransportPolicy === 'relay' || pcConfig.iceTransportPolicy === 'all'
      ? { iceTransportPolicy: pcConfig.iceTransportPolicy }
      : {})
  };
  // Never force bundle/rtcpMux — leave browser + JsSIP defaults for FreeSWITCH.
  delete next.bundlePolicy;
  delete next.rtcpMuxPolicy;
  next.iceCandidatePoolSize = 0;
  return next;
}

export function summarizeDisconnectEvent(event) {
  if (!event || typeof event !== 'object') {
    return {};
  }
  return {
    code: event.code ?? event.error?.code,
    reason: event.reason ?? event.error?.reason,
    cause: event.cause ?? event.error?.cause
  };
}

/**
 * Firefox-only ICE failures (stuck checking / zero RTP while Chrome connects with the same
 * config) are usually decided by the SDP inventory: ice-lite, rtcp-mux, missing candidates,
 * private-only or mDNS addresses. Summarize any description (offer or answer) for logs —
 * never includes ufrag/pwd/fingerprint values, only their presence.
 *
 * @param {string|undefined} sdp
 * @returns {object|null}
 */
export function summarizeSdp(sdp) {
  if (typeof sdp !== 'string' || !sdp.trim()) return null;
  const lines = sdp.split(/\r?\n/);
  const cLine = lines.find((line) => /^c=/i.test(line));
  /** @type {Record<string, number>} */
  const candidatesByType = {};
  /** @type {Set<string>} */
  const ips = new Set();
  let candidateCount = 0;
  let tcpCandidateCount = 0;
  lines.forEach((line) => {
    if (!/^a=candidate:/i.test(line)) return;
    candidateCount += 1;
    // candidate:<foundation> <component> <transport> <priority> <address> <port> typ <type>
    const match = /^a=candidate:\S+\s+\d+\s+(\S+)\s+\d+\s+(\S+)\s+(\d+)\s+typ\s+(\S+)/i.exec(line);
    const protocol = match?.[1]?.toLowerCase() ?? 'unknown';
    const address = match?.[2] ?? '';
    const type = match?.[4]?.toLowerCase() ?? 'unknown';
    if (protocol === 'tcp') tcpCandidateCount += 1;
    candidatesByType[type] = (candidatesByType[type] ?? 0) + 1;
    if (address && !/\.local$/i.test(address)) ips.add(`${address}:${match?.[3] ?? ''}`);
  });
  return {
    mLineCount: lines.filter((line) => /^m=/i.test(line)).length,
    cAddress: cLine?.replace(/^c=/i, '').trim().split(/\s+/)[2] ?? null,
    iceLite: lines.some((line) => /^a=ice-lite/i.test(line)),
    hasUfrag: lines.some((line) => /^a=ice-ufrag:/i.test(line)),
    hasPwd: lines.some((line) => /^a=ice-pwd:/i.test(line)),
    hasDtlsFingerprint: lines.some((line) => /^a=fingerprint:/i.test(line)),
    rtcpMux: lines.some((line) => /^a=rtcp-mux/i.test(line)),
    setup: (/^a=setup:(\S+)/im.exec(sdp)?.[1]) ?? null,
    endOfCandidates: lines.some((line) => /^a=end-of-candidates/i.test(line)),
    candidateCount,
    tcpCandidateCount,
    candidatesByType,
    candidateAddresses: [...ips].slice(0, 8)
  };
}

/**
 * One-line rendering of a summarizeSdp() inventory so decisive fields stay visible even when
 * the browser console collapses nested objects in pasted logs.
 *
 * @param {object|null|undefined} summary
 * @returns {string}
 */
export function describeSdpSummary(summary) {
  if (!summary) return 'none';
  const types = Object.entries(summary.candidatesByType || {})
    .map(([type, count]) => `${type}x${count}`)
    .join(',') || 'none';
  return [
    `m=${summary.mLineCount ?? '?'}`,
    `c=${summary.cAddress ?? '?'}`,
    `iceLite=${Boolean(summary.iceLite)}`,
    `mux=${Boolean(summary.rtcpMux)}`,
    `setup=${summary.setup || '?'}`,
    `eoc=${Boolean(summary.endOfCandidates)}`,
    `cand=${summary.candidateCount ?? 0}(${types})`,
    `tcp=${summary.tcpCandidateCount ?? 0}`,
    `ips=[${(summary.candidateAddresses || []).join(' ')}]`
  ].join(' ');
}
